<template>
  <div class="email-array-input">
    <v-text-field
        v-model="inputEmail"
        :label="label"
        :placeholder="placeholder"
        :rules="computedRules"
        :disabled="disabled"
        :readonly="readonly"
        :hide-details="hideDetails && !errorMessage"
        :error-messages="errorMessage"
        @keydown.enter.prevent="addEmail"
        @blur="addEmail"
        type="email"
        :prepend-icon="prependIcon"
        :outlined="outlined"
        :solo="solo"
        :clearable="clearable"
        ref="input"
    >
      <template #append>
        <v-icon
            v-if="errorMessage"
            color="error"
            small
        >
          mdi-alert-circle-outline
        </v-icon>
        <v-btn
            v-else
            icon
            small
            @click="addEmail"
            :disabled="!inputEmail || disabled"
            color="primary"
        >
          <v-icon small>mdi-plus</v-icon>
        </v-btn>
      </template>
    </v-text-field>

    <div v-if="hint && !errorMessage" class="v-messages theme--light">
      <div class="v-messages__wrapper">
        <div class="v-messages__message">{{ hint }}</div>
      </div>
    </div>

    <div class="email-chips-container" v-if="emails.length > 0">
      <v-chip
          v-for="(email, index) in emails"
          :key="`email-${index}`"
          close
          @click:close="removeEmail(index)"
          small
          outlined
          class="ma-1"
      >
        {{ email }}
      </v-chip>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EmailArrayInput',

  data() {
    return {
      emails: this.value || [],
      inputEmail: '',
      errorMessage: '',
    }
  },

  model: {
    event: 'input',
    prop: 'value',
  },

  props: {
    value: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: 'Email'
    },
    placeholder: {
      type: String,
      default: 'example@mail.com'
    },
    rules: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    prependIcon: {
      type: String,
      default: ''
    },
    outlined: {
      type: Boolean,
      default: false
    },
    solo: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    hint: {
      type: String,
      default: ''
    },
    maxEmails: {
      type: Number,
      default: null
    }
  },

  watch: {
    value(val) {
      this.emails = val || []
    },

    emails: {
      handler(val) {
        this.$emit('input', val)
      },
      deep: true
    },

    inputEmail(val) {
      if (this.errorMessage) {
        this.errorMessage = ''
        this.$emit('update:error', false)
      }
    },

    errorMessage(val) {
      this.$emit('update:error', !!val)
    }
  },

  computed: {
    computedRules() {
      return []
    },

    errorBucket() {
      // Expose error messages for FormField compatibility
      return this.errorMessage ? [this.errorMessage] : []
    }
  },

  methods: {
    validateEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },

    addEmail() {
      const email = this.inputEmail.trim()

      if (!email) {
        return
      }

      if (!this.validateEmail(email)) {
        this.errorMessage = this.$t('rules.email-not-valid')
        return
      }

      if (this.emails.includes(email)) {
        this.errorMessage = this.$t('rules.email-already-in-list')
        return
      }

      if (this.maxEmails && this.emails.length >= this.maxEmails) {
        this.errorMessage = this.$t('rules.max-emails-allowed', { max: this.maxEmails })
        return
      }

      this.emails.push(email)
      this.inputEmail = ''
      this.errorMessage = ''

      this.$nextTick(() => {
        if (this.$refs.input) {
          this.$refs.input.focus()
        }
      })
    },

    removeEmail(index) {
      this.emails.splice(index, 1)
    }
  }
}
</script>

<style lang="scss">
.email-array-input {
  .email-chips-container {
    margin-top: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  .v-messages {
    font-size: 12px;
    padding-top: 8px;
    min-height: 14px;

    &__wrapper {
      color: rgba(0, 0, 0, 0.6);
    }
  }
}
</style>